import { maybe, Maybe, rd } from '@passionware/monads';
import { isEqual, omit } from 'lodash';
import {
  AtellioFeatureGroupDefinition,
  featureFlags,
  localStorageCleanupVersionMapping
} from 'v5/features';
import {
  FeatureFlagValues,
  FeatureFlagWritableSource
} from 'v5/platform/feature-management/feature-management';
import { WithServices } from 'v5/platform/ts/services';
import { FeatureFlagSet } from 'v5/api/enterprise-feature-flag/enterprise-feature-flag.api';
import { EnterpriseFeatureFlagService } from 'v5/services/io/EnterpriseFeatureFlagService/EnterpriseFeatureFlagService';
import { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { create } from 'zustand';
import { FeatureFlagEnum } from '../../../../dtos/constants/feature-flag.enum';
import { WithToastService } from '../ToastService/ToastService';
import { FeatureManagementService } from './FeatureManagementService';
import {
  FeatureFlagConfig,
  FeatureFlagEntry,
  FeatureFlagValueConfig,
  FeatureFlagView,
  FeatureManagementAction,
  FeatureManagementSource
} from './types';

type Store = {
  actions: FeatureManagementAction[];
  addAction: (action: FeatureManagementAction) => void;
};

function namesToValues(
  names: string[]
): Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>> {
  return Object.fromEntries(names.map(name => [name, true]));
}

function valuesToNames(
  values: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>
): string[] {
  return Object.entries(values)
    .filter(([, value]) => Boolean(value))
    .map(([name]) => name);
}

export function featureFlagSetFromState(
  state: FeatureManagementState
): FeatureFlagSet {
  return {
    legacy: valuesToNames(state.legacy),
    byWorkspace: Object.fromEntries(
      Object.entries(state.byWorkspace).map(([workspaceId, values]) => [
        Number(workspaceId),
        valuesToNames(values)
      ])
    )
  };
}

export type FeatureManagementState = {
  legacy: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
  byWorkspace: Record<
    number,
    Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>
  >;
  frontend: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
};

export function stateFromFlagSet(
  flagSet: FeatureFlagSet,
  frontend: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>
): FeatureManagementState {
  return {
    legacy: namesToValues(flagSet.legacy),
    byWorkspace: Object.fromEntries(
      Object.entries(flagSet.byWorkspace).map(([workspaceId, names]) => [
        Number(workspaceId),
        namesToValues(names)
      ])
    ),
    frontend
  };
}

function collectChangedFlagIds(
  before: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>,
  after: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>
): string[] {
  const ids = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...ids].filter(id => !isEqual(before[id], after[id]));
}

/**
 * Diff base vs optimistic state into backend (legacy + workspace) and frontend
 * feature-id lists. Used by the management UI for dirty badges and the save ack.
 */
export function diffFeatureManagementState(
  base: FeatureManagementState,
  next: FeatureManagementState
): {
  changedBackendFlagIds: string[];
  changedFrontendFlagIds: string[];
  changedLegacyFlagIds: string[];
  changedWorkspaceFlagIds: Record<number, string[]>;
} {
  const changedLegacyFlagIds = collectChangedFlagIds(
    base.legacy,
    next.legacy
  ).sort();
  const backendIds = new Set<string>(changedLegacyFlagIds);

  const workspaceIds = new Set([
    ...Object.keys(base.byWorkspace),
    ...Object.keys(next.byWorkspace)
  ]);

  const changedWorkspaceFlagIds: Record<number, string[]> = {};

  for (const workspaceId of workspaceIds) {
    const id = Number(workspaceId);
    const changed = collectChangedFlagIds(
      base.byWorkspace[id] ?? {},
      next.byWorkspace[id] ?? {}
    ).sort();
    if (changed.length > 0) {
      changedWorkspaceFlagIds[id] = changed;
    }
    for (const featureId of changed) {
      backendIds.add(featureId);
    }
  }

  return {
    changedBackendFlagIds: [...backendIds].sort(),
    changedFrontendFlagIds: collectChangedFlagIds(
      base.frontend,
      next.frontend
    ).sort(),
    changedLegacyFlagIds,
    changedWorkspaceFlagIds
  };
}

export function isBackendStateEqual(
  base: FeatureManagementState,
  next: FeatureManagementState
): boolean {
  return (
    isEqual(base.legacy, next.legacy) &&
    isEqual(base.byWorkspace, next.byWorkspace)
  );
}

function applySourceUpdate(
  container: FeatureManagementState,
  source: FeatureManagementSource,
  featureId: string,
  value: FeatureFlagValueConfig<false>['current']
): FeatureManagementState {
  if (source === 'legacy') {
    return {
      ...container,
      legacy: {
        ...container.legacy,
        [featureId]: value
      }
    };
  }

  if (source === 'frontend') {
    return {
      ...container,
      frontend: {
        ...container.frontend,
        [featureId]: value
      }
    };
  }

  const workspaceValues = container.byWorkspace[source.workspaceId] ?? {};

  return {
    ...container,
    byWorkspace: {
      ...container.byWorkspace,
      [source.workspaceId]: {
        ...workspaceValues,
        [featureId]: value
      }
    }
  };
}

function applySourceClear(
  container: FeatureManagementState,
  source: FeatureManagementSource,
  featureId: string
): FeatureManagementState {
  if (source === 'legacy') {
    return {
      ...container,
      legacy: omit(container.legacy, featureId)
    };
  }

  if (source === 'frontend') {
    return {
      ...container,
      frontend: omit(container.frontend, featureId)
    };
  }

  const workspaceValues = container.byWorkspace[source.workspaceId] ?? {};

  return {
    ...container,
    byWorkspace: {
      ...container.byWorkspace,
      [source.workspaceId]: omit(workspaceValues, featureId)
    }
  };
}

export function createFeatureManagementService(
  config: WithServices<[WithToastService]> & {
    enterpriseFeatureFlagService: EnterpriseFeatureFlagService;
    enterpriseService: EnterpriseService;
    localStorageSource: FeatureFlagWritableSource<AtellioFeatureGroupDefinition>;
    enforcedVersions: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
    getOrganizationId: () => Maybe<string>;
  }
): FeatureManagementService {
  const useStore = create<Store>(setState => ({
    actions: [],
    addAction: action =>
      setState(state => ({
        actions: [...state.actions, action]
      }))
  }));

  return {
    useFeatureFlagConfigView: () => {
      const organizationId = config.getOrganizationId();
      const flagSetRd =
        config.enterpriseFeatureFlagService.useFlagSet(organizationId);
      const workspacesRd = config.enterpriseService.useWorkspaces(
        organizationId,
        'active'
      );
      const frontend = config.localStorageSource.useFeatureFlags();
      const actions = useStore(state => state.actions);

      return rd.useMemoMap(
        rd.useCombine({
          flagSet: flagSetRd,
          workspaces: workspacesRd,
          frontend
        }),
        ({ flagSet, workspaces, frontend }, actions) => {
          const baseState = stateFromFlagSet(flagSet, frontend);

          const optimisticState = actions.reduce(
            featureManagementReducer,
            baseState
          );

          const workspaceColumns = (workspaces ?? []).map(workspace => ({
            id: workspace.id,
            name: workspace.name
          }));

          const {
            changedBackendFlagIds,
            changedFrontendFlagIds,
            changedLegacyFlagIds,
            changedWorkspaceFlagIds
          } = diffFeatureManagementState(baseState, optimisticState);

          return {
            featureFlagView: computeView(
              optimisticState,
              workspaceColumns,
              config.enforcedVersions
            ),
            isDirty: !isEqual(baseState, optimisticState),
            changedBackendFlagIds,
            changedFrontendFlagIds,
            changedLegacyFlagIds,
            changedWorkspaceFlagIds
          };
        },
        actions
      );
    },
    scheduleAction: action => {
      useStore.getState().addAction(action);
    },
    saveConfig: async ({ successToast = true } = {}) => {
      const organizationId = config.getOrganizationId();
      if (maybe.isAbsent(organizationId)) {
        return;
      }
      const actions = useStore.getState().actions;
      const flagSet =
        await config.enterpriseFeatureFlagService.ensureFlagSet(organizationId);

      const baseState = stateFromFlagSet(
        flagSet,
        await config.localStorageSource.getFeatureFlags()
      );

      const nextState = actions.reduce(featureManagementReducer, baseState);
      const frontendChanges: Partial<
        FeatureFlagValues<AtellioFeatureGroupDefinition>
      > = {};

      actions.forEach(action => {
        switch (action.type) {
          case 'setFeatureFlag':
            if (action.source === 'frontend') {
              frontendChanges[action.featureId] = action.value;
            }
            break;
          case 'clearFeatureFlag':
            if (action.source === 'frontend') {
              frontendChanges[action.featureId] = null;
            }
            break;
        }
      });

      const backendChanged = !isBackendStateEqual(baseState, nextState);

      try {
        if (backendChanged) {
          await config.enterpriseFeatureFlagService.saveFlagSet(
            organizationId,
            featureFlagSetFromState(nextState)
          );
        }

        if (Object.keys(frontendChanges).length > 0) {
          await config.localStorageSource.save(frontendChanges);
        }

        useStore.setState({ actions: [] });
        if (successToast) {
          config.services.toastService.open({
            type: 'success',
            message: 'Feature flags saved.'
          });
        }
      } catch {
        config.services.toastService.open({
          type: 'error',
          message: 'Feature flags save failed.'
        });
      }
    },
    discardChanges: () => {
      useStore.setState({ actions: [] });
    },
    getEnforcedFeatureFlagConfig: () => config.enforcedVersions
  };
}

export function featureManagementReducer(
  view: FeatureManagementState,
  action: FeatureManagementAction
): FeatureManagementState {
  switch (action.type) {
    case 'setFeatureFlag':
      return applySourceUpdate(
        view,
        action.source,
        action.featureId,
        action.value
      );
    case 'clearFeatureFlag':
      return applySourceClear(view, action.source, action.featureId);
  }
}

/**
 * The stored flag set only enumerates enabled names, so an unlisted flag is off
 * and the server-side default is always false — the spec default applies to
 * frontend overrides only.
 */
function serverFlagConfig(isEnabled: boolean): FeatureFlagConfig {
  return {
    supported: true,
    optional: false,
    value: {
      type: 'boolean',
      current: isEnabled,
      defaultValue: false
    }
  };
}

const unsupportedFlagConfig: FeatureFlagConfig = { supported: false };

export function computeView(
  { legacy, byWorkspace, frontend }: FeatureManagementState,
  workspaceColumns: FeatureFlagView['workspaceColumns'],
  enforcedVersions: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>
): FeatureFlagView {
  return {
    workspaceColumns,
    groups: featureFlags.groups.map(group => ({
      name: group.name,
      entries: group.entries.map<FeatureFlagEntry>(entry => {
        const isServerFlag =
          entry.flagSpec.type === 'boolean' &&
          Object.values(FeatureFlagEnum).includes(entry.id);

        return {
          featureId: entry.id,
          featureName: entry.name,
          description: entry.description,
          shortcut: entry.shortcut,
          isBypassed: maybe.isPresent(enforcedVersions[entry.id]),
          config: {
            legacy: isServerFlag
              ? serverFlagConfig(legacy[entry.id] === true)
              : unsupportedFlagConfig,
            workspaces: Object.fromEntries(
              workspaceColumns.map(column => [
                column.id,
                isServerFlag
                  ? serverFlagConfig(
                      byWorkspace[column.id]?.[entry.id] === true
                    )
                  : unsupportedFlagConfig
              ])
            ),
            frontend:
              entry.id in localStorageCleanupVersionMapping
                ? ({
                    supported: true,
                    optional: true,
                    value: {
                      ...entry.flagSpec,
                      current: maybe.getOrNull(frontend[entry.id]),
                      defaultValue: null
                    }
                  } satisfies FeatureFlagConfig)
                : unsupportedFlagConfig
          }
        };
      })
    }))
  };
}
